// ============================================================
// Document ingest — BROWSER parser + redactor (pdf.js text + tesseract OCR for scans)
// Per-page text; pages with sparse text are full-page OCR'd. Redacts bank/brokerage
// account numbers, SSNs, and EINs (preserves license/permit/DEA numbers). Mirrors the
// sandbox docparse.py rules. Output matches the ingest-document-corpus contract.
// ============================================================

import * as pdfjsLib from "pdfjs-dist";
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import Tesseract from "tesseract.js";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

export type DocCategory = "trust_corporate" | "financial" | "public_regulatory" | "document";

export interface DocRedaction { type: "ssn" | "ein" | "account" }
export interface RedactionOriginal { page: number; type: string; original: string }
export interface ParsedDocPage { page: number; text: string; mode: "text" | "ocr" | "empty"; redactions: DocRedaction[] }
export interface ParsedDoc {
  source_label: string;
  category: DocCategory;
  instrument_type: string | null;
  doc_date: string | null;
  page_count: number;
  redaction_total: number;
  redaction_types: Record<string, number>;
  redaction_originals: RedactionOriginal[];
  pages: ParsedDocPage[];
}

export interface DocParseOptions { onProgress?: (phase: string, pct?: number) => void }

const TEXT_FLOOR = 60; // pages with < this many chars get OCR'd

interface Span { type: "ssn" | "ein" | "account"; original: string }

/** Redact SSN/EIN (absolute) + bank/brokerage account numbers (contextual, near an account keyword).
 *  Preserves license/permit/DEA numbers (they aren't near account keywords and aren't SSN/EIN shaped). */
export function redactText(input: string): { text: string; spans: Span[] } {
  const spans: Span[] = [];
  let text = input.replace(/\b\d{3}-\d{2}-\d{4}\b/g, (m) => { spans.push({ type: "ssn", original: m }); return "[REDACTED:ssn]"; });
  text = text.replace(/\b\d{2}-\d{7}\b/g, (m) => { spans.push({ type: "ein", original: m }); return "[REDACTED:ein]"; });

  const toRedact = new Set<string>();
  const kwRe = /(account|acct|a\/c|checking|savings|brokerage|truist|routing|aba|iban)/gi;
  for (const kw of text.matchAll(kwRe)) {
    const start = (kw.index ?? 0) + kw[0].length;
    const window = text.slice(start, start + 45);
    const dm = window.match(/\b\d[\d \-]{4,}\d\b/);
    if (dm) {
      const digits = dm[0].replace(/[ \-]/g, "");
      if (digits.length >= 5 && digits.length <= 17) toRedact.add(dm[0]);
    }
  }
  for (const orig of toRedact) {
    spans.push({ type: "account", original: orig.trim() });
    text = text.split(orig).join("[REDACTED:account]");
  }
  return { text, spans };
}

async function pageText(pdf: pdfjsLib.PDFDocumentProxy, p: number, opts: DocParseOptions): Promise<{ text: string; mode: "text" | "ocr" | "empty" }> {
  const page = await pdf.getPage(p);
  const content = await page.getTextContent();
  let txt = "";
  for (const item of content.items as Array<{ str: string; hasEOL?: boolean }>) {
    txt += item.str + (item.hasEOL ? "\n" : " ");
  }
  if (txt.trim().length >= TEXT_FLOOR) return { text: txt, mode: "text" };
  opts.onProgress?.(`ocr page ${p}`);
  const scale = 200 / 72;
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement("canvas");
  canvas.width = Math.ceil(viewport.width);
  canvas.height = Math.ceil(viewport.height);
  const ctx = canvas.getContext("2d");
  if (!ctx) return { text: txt, mode: "empty" };
  await page.render({ canvasContext: ctx, viewport }).promise;
  const { data } = await Tesseract.recognize(canvas, "eng");
  return { text: data.text || "", mode: "ocr" };
}

export async function parseDocumentPdf(
  file: File,
  category: DocCategory,
  instrumentType: string | null,
  opts: DocParseOptions = {},
): Promise<ParsedDoc> {
  opts.onProgress?.("loading");
  const buf = new Uint8Array(await file.arrayBuffer());
  const pdf = await pdfjsLib.getDocument({ data: buf }).promise;

  const pages: ParsedDocPage[] = [];
  const originals: RedactionOriginal[] = [];
  const types: Record<string, number> = {};
  let total = 0;

  for (let p = 1; p <= pdf.numPages; p++) {
    opts.onProgress?.(`page ${p} of ${pdf.numPages}`, Math.round((p / pdf.numPages) * 100));
    const { text: raw, mode } = await pageText(pdf, p, opts);
    const { text, spans } = redactText(raw);
    for (const s of spans) {
      types[s.type] = (types[s.type] ?? 0) + 1;
      originals.push({ page: p, type: s.type, original: s.original });
    }
    total += spans.length;
    pages.push({ page: p, text, mode, redactions: spans.map((s) => ({ type: s.type })) });
  }

  opts.onProgress?.("done", 100);
  return {
    source_label: file.name,
    category,
    instrument_type: instrumentType,
    doc_date: null,
    page_count: pdf.numPages,
    redaction_total: total,
    redaction_types: types,
    redaction_originals: originals,
    pages,
  };
}
